import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, IconButton, TextField, InputAdornment, Avatar, Menu, MenuItem, Divider, Tooltip, Badge, Typography, List, ListItem, ListItemText, Paper, ClickAwayListener, Chip, useTheme } from '@mui/material';
import {
  Search as SearchIcon,
  Menu as MenuIcon,
  Notifications as NotificationsIcon,
  AdminPanelSettings as AdminPanelIcon,
  DoneAll as DoneAllIcon,
  DeleteSweep as ClearAllIcon,
  Close as CloseIcon,
  History as HistoryIcon,
  Person as PersonIcon,
  Subject as SubjectIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../stores/authStore';
import { useMailStore } from '../../stores/mailStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { getInitials } from '../../utils/helpers';
import { useBranding } from '../../utils/BrandingContext';

const RECENT_SEARCHES_KEY = 'cruvz_recent_searches';
const MAX_RECENT = 5;

interface HybridHeaderProps {
  onMenuClick?: () => void;
  isMobile?: boolean;
}

const HybridHeader: React.FC<HybridHeaderProps> = ({ onMenuClick, isMobile = false }) => {
  const navigate = useNavigate();
  const muiTheme = useTheme();
  const isDark = muiTheme.palette.mode === 'dark';
  const { user, logout } = useAuthStore();
  const { emails, setSearchQuery } = useMailStore();
  const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll } = useNotificationStore();
  const { branding } = useBranding()
  ;
  const [searchValue, setSearchValue] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]'); } catch { return []; }
  });
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);

  // Derive unique senders from loaded emails
  const knownSenders = useMemo(() => {
    const seen = new Map<string, string>();
    emails.forEach((e: any) => {
      const addr = typeof e.from === 'string' ? e.from : (e.from?.address || '');
      const name = typeof e.from === 'string' ? '' : (e.from?.name || '');
      if (addr && !seen.has(addr)) seen.set(addr, name);
    });
    return Array.from(seen.entries()).map(([addr, name]) => ({ addr, name }));
  }, [emails]);

  // Operator-aware suggestions
  const suggestions = useMemo(() => {
    const raw = searchValue.trim();
    const q = raw.toLowerCase();
    type OpResult = { label: string; value: string; secondary?: string };

    if (!q) return {
      recent: recentSearches,
      operatorResults: [] as OpResult[],
      matchedEmails: [] as typeof emails,
      operatorHints: [] as string[],
    };

    // from:X
    if (q.startsWith('from:')) {
      const term = q.slice(5).trim();
      const results = knownSenders
        .filter(s => !term || s.addr.includes(term) || s.name.toLowerCase().includes(term))
        .slice(0, 8)
        .map(s => ({ label: s.name ? `${s.name} <${s.addr}>` : s.addr, value: `from:${s.addr}`, secondary: s.addr }));
      return { recent: [], operatorResults: results, matchedEmails: [], operatorHints: [] };
    }

    // to:X
    if (q.startsWith('to:')) {
      const term = q.slice(3).trim();
      const addrSet = new Set<string>();
      emails.forEach((e: any) => {
        const toRaw = e.to;
        const addrs: string[] = [];
        if (Array.isArray(toRaw)) toRaw.forEach((t: any) => addrs.push(typeof t === 'string' ? t : (t?.address || '')));
        else if (typeof toRaw === 'string') addrs.push(toRaw);
        else if (toRaw?.address) addrs.push(toRaw.address);
        addrs.filter(Boolean).forEach(a => { const al = a.toLowerCase(); if (!term || al.includes(term)) addrSet.add(al); });
      });
      const results: OpResult[] = Array.from(addrSet).slice(0, 8).map(addr => ({ label: addr, value: `to:${addr}`, secondary: 'recipient' }));
      return { recent: [], operatorResults: results, matchedEmails: [], operatorHints: [] };
    }

    // subject:X
    if (q.startsWith('subject:')) {
      const term = q.slice(8).trim();
      const seen = new Set<string>();
      const results: OpResult[] = [];
      emails.forEach((e: any) => {
        const s = (e.subject || '').trim();
        if (s && !seen.has(s) && (!term || s.toLowerCase().includes(term))) {
          seen.add(s);
          const fromName = typeof e.from === 'string' ? e.from : (e.from?.name || e.from?.address || '');
          results.push({ label: s, value: `subject:${s}`, secondary: `from ${fromName}` });
        }
      });
      return { recent: [], operatorResults: results.slice(0, 8), matchedEmails: [], operatorHints: [] };
    }

    // Plain text: recent + email previews + operator hints
    const matchedRecent = recentSearches.filter(r => r.toLowerCase().includes(q)).slice(0, 3);
    const matchedEmails = emails.filter((e: any) => {
      const sub = (e.subject || '').toLowerCase();
      const fromAddr = typeof e.from === 'string' ? e.from.toLowerCase() : (e.from?.address || '').toLowerCase();
      const fromName = typeof e.from === 'string' ? '' : (e.from?.name || '').toLowerCase();
      return sub.includes(q) || fromAddr.includes(q) || fromName.includes(q);
    }).slice(0, 5);
    const operatorHints = [`from:${raw}`, `to:${raw}`, `subject:${raw}`];
    return { recent: matchedRecent, operatorResults: [], matchedEmails, operatorHints };
  }, [searchValue, recentSearches, knownSenders, emails]);

  const hasSuggestions = suggestions.recent.length > 0 || suggestions.operatorResults.length > 0 || suggestions.matchedEmails.length > 0;

  const saveRecentSearch = (q: string) => {
    const updated = [q, ...recentSearches.filter(r => r !== q)].slice(0, MAX_RECENT);
    setRecentSearches(updated);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  };

  const executeSearch = (q: string) => {
    if (!q.trim()) return;
    saveRecentSearch(q.trim());
    setSearchValue(q.trim());
    setSearchQuery(q.trim());
    setShowSuggestions(false);
    navigate('/mail/inbox');
    searchInputRef.current?.blur();
  };

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    executeSearch(searchValue);
  };

  const handleSearchClear = () => {
    setSearchValue('');
    setSearchQuery('');
    setShowSuggestions(false);
    searchInputRef.current?.focus();
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleSearchClear();
      searchInputRef.current?.blur();
      setSearchFocused(false);
      setShowSuggestions(false);
    }
    if (e.key === 'ArrowDown') e.preventDefault();
  };

  const handleLogout = () => {
    setUserMenuAnchor(null);
    logout();
    navigate('/login');
  };

  return (
    <Box
      className="header"
      sx={{
        background: muiTheme.palette.background.paper,
        borderBottom: `1px solid ${muiTheme.palette.divider}`,
        height: isMobile ? '56px' : '64px',
        display: 'flex',
        alignItems: 'center',
        padding: isMobile ? '0 12px' : '0 20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        position: 'sticky',
        top: 0,
        zIndex: 1100,
      }}
    >
      {/* Menu toggle button */}
      {onMenuClick && (
        <IconButton
          onClick={onMenuClick}
          sx={{
            width: isMobile ? '40px' : '36px',
            height: isMobile ? '40px' : '36px',
            borderRadius: '8px',
            mr: isMobile ? 1 : 2,
            '&:hover': { background: '#f3f4f6' },
          }}
        >
          <MenuIcon fontSize={isMobile ? 'medium' : 'small'} />
        </IconButton>
      )}

      {/* Logo Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: 'pointer',
          flexShrink: 0,
        }}
        onClick={() => navigate('/mail/inbox')}
      >
        {branding.logo ? (
          /* Custom uploaded logo */
          <Box
            component="img"
            src={branding.logo}
            alt={branding.companyName || 'Logo'}
            sx={{
              height: isMobile ? '40px' : '70px',
              width: 'auto',
              maxWidth: isMobile ? '140px' : '260px',
              display: 'block',
              objectFit: 'contain',
            }}
          />
        ) : branding.companyName && branding.companyName !== 'CruvzMail' ? (
          /* Custom domain without logo — gradient text fallback */
          <Box
            sx={{
              fontSize: '1.5rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              letterSpacing: '-0.3px',
            }}
          >
            {branding.companyName}
          </Box>
        ) : (
          /* Default CruvzMail logo */
          <Box
            component="img"
            src="/logo-hd.png"
            alt="CruvzMail"
            sx={{
              height: isMobile ? '40px' : '70px',
              width: 'auto',
              maxWidth: isMobile ? '140px' : '260px',
              display: 'block',
              objectFit: 'contain',
            }}
          />
        )}
      </Box>

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Search Bar - Hide on small mobile, show as icon */}
      {!isMobile ? (
        <ClickAwayListener onClickAway={() => { setShowSuggestions(false); setSearchFocused(false); }}>
          <Box
            component="form"
            onSubmit={handleSearch}
            sx={{
              flexGrow: searchFocused ? 1 : 0,
              width: searchFocused ? '100%' : '420px',
              maxWidth: '680px',
              mr: 2,
              transition: 'all 0.2s ease',
              position: 'relative',
            }}
          >
            <TextField
              inputRef={searchInputRef}
              size="small"
              fullWidth
              placeholder="Search mail, contacts, or type from: to: subject:"
              value={searchValue}
              onChange={(e) => { setSearchValue(e.target.value); setShowSuggestions(true); }}
              onFocus={() => { setSearchFocused(true); setShowSuggestions(true); }}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                  <IconButton size="small" onClick={() => handleSearch()} sx={{ p: 0.25, color: searchFocused ? '#1a73e8' : '#9ca3af' }}>
                    <SearchIcon sx={{ fontSize: 20 }} />
                  </IconButton>
                </InputAdornment>
              ),
              endAdornment: searchValue ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleSearchClear} sx={{ p: 0.25, color: '#9ca3af', '&:hover': { color: '#4a5568' } }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                borderRadius: '24px',
                background: searchFocused ? '#fff' : '#f1f3f4',
                border: '1px solid transparent',
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&:hover': { background: '#e8eaed' },
                '&.Mui-focused': {
                  background: '#fff',
                  borderColor: '#c5cae9',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.2), 0 4px 8px rgba(0,0,0,0.1)',
                },
                transition: 'all 0.2s ease',
                fontSize: '0.9rem',
              },
            }}
          />

          {/* Suggestions Dropdown */}
          {showSuggestions && searchFocused && (hasSuggestions || !searchValue) && (
            <Paper
              elevation={4}
              sx={{
                position: 'absolute',
                top: 'calc(100% + 4px)',
                left: 0,
                right: 0,
                borderRadius: '12px',
                border: '1px solid #e0e4e8',
                boxShadow: '0 4px 24px rgba(0,0,0,0.13)',
                zIndex: 1500,
                overflow: 'hidden',
                maxHeight: 420,
                overflowY: 'auto',
              }}
            >
              {/* Quick filter chips — shown when input is empty */}
              {!searchValue && (
                <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #f1f3f4' }}>
                  <Typography sx={{ fontSize: '0.72rem', color: '#80868b', fontWeight: 600, mb: 1, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Search by</Typography>
                  <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                    {['from:', 'to:', 'subject:', 'has:attachment', 'is:unread', 'is:starred'].map(chip => (
                      <Chip
                        key={chip}
                        label={chip}
                        size="small"
                        onClick={() => { setSearchValue(chip); setShowSuggestions(true); setTimeout(() => searchInputRef.current?.focus(), 0); }}
                        sx={{ fontSize: '0.78rem', height: 26, bgcolor: '#f1f3f4', '&:hover': { bgcolor: '#e8eaed' }, cursor: 'pointer' }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Recent Searches */}
              {suggestions.recent.length > 0 && (
                <Box>
                  <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: '0.72rem', color: '#80868b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Recent</Typography>
                  {suggestions.recent.map((r) => (
                    <ListItem key={r} onClick={() => executeSearch(r)}
                      sx={{ px: 2, py: 0.75, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, gap: 1.5 }}
                    >
                      <HistoryIcon sx={{ fontSize: 16, color: '#9aa0a6', flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '0.875rem', color: '#3c4043' }}>{r}</Typography>
                    </ListItem>
                  ))}
                </Box>
              )}

              {/* Operator-specific results: from:, to:, subject: */}
              {suggestions.operatorResults.length > 0 && (
                <Box>
                  {suggestions.operatorResults.map((r) => (
                    <ListItem key={r.value} onClick={() => executeSearch(r.value)}
                      sx={{ px: 2, py: 0.75, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, gap: 1.5 }}
                    >
                      {searchValue.toLowerCase().startsWith('subject:') ? (
                        <SubjectIcon sx={{ fontSize: 16, color: '#9aa0a6', flexShrink: 0 }} />
                      ) : (
                        <Avatar sx={{ width: 28, height: 28, fontSize: '0.65rem', bgcolor: '#1a73e8', flexShrink: 0 }}>
                          {getInitials(r.label)}
                        </Avatar>
                      )}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: '0.875rem', color: '#3c4043', lineHeight: 1.3 }}>{r.label}</Typography>
                        {r.secondary && r.secondary !== r.label && (
                          <Typography sx={{ fontSize: '0.75rem', color: '#80868b' }}>{r.secondary}</Typography>
                        )}
                      </Box>
                    </ListItem>
                  ))}
                </Box>
              )}

              {/* Matching email preview rows */}
              {suggestions.matchedEmails.length > 0 && (
                <Box sx={{ borderTop: suggestions.recent.length > 0 ? '1px solid #f1f3f4' : 'none' }}>
                  <Typography sx={{ px: 2, pt: 1.5, pb: 0.5, fontSize: '0.72rem', color: '#80868b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Emails</Typography>
                  {suggestions.matchedEmails.map((e: any) => {
                    const fromAddr = typeof e.from === 'string' ? e.from : (e.from?.address || '');
                    const fromName = typeof e.from === 'string' ? '' : (e.from?.name || '');
                    const displayName = fromName || fromAddr;
                    const dateStr = e.date ? new Date(e.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';
                    return (
                      <ListItem key={e.id} onClick={() => executeSearch(e.subject || fromAddr)}
                        sx={{ px: 2, py: 0.75, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, gap: 1.5, alignItems: 'flex-start' }}
                      >
                        <Avatar sx={{ width: 28, height: 28, mt: 0.25, fontSize: '0.65rem', bgcolor: '#34a853', flexShrink: 0 }}>
                          {getInitials(displayName)}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography noWrap sx={{ fontSize: '0.85rem', fontWeight: e.isRead ? 400 : 600, color: '#202124', flex: 1 }}>{displayName}</Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: '#9aa0a6', flexShrink: 0, ml: 1 }}>{dateStr}</Typography>
                          </Box>
                          <Typography noWrap sx={{ fontSize: '0.78rem', color: '#5f6368' }}>{e.subject}</Typography>
                        </Box>
                      </ListItem>
                    );
                  })}
                </Box>
              )}

              {/* Operator hints for plain text (from:X, to:X, subject:X) */}
              {(suggestions.operatorHints || []).length > 0 && searchValue && (
                <Box sx={{ borderTop: '1px solid #f1f3f4' }}>
                  {(suggestions.operatorHints || []).map((hint) => {
                    const [op] = hint.split(':');
                    return (
                      <ListItem key={hint} onClick={() => executeSearch(hint)}
                        sx={{ px: 2, py: 0.6, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, gap: 1.5 }}
                      >
                        <SearchIcon sx={{ fontSize: 15, color: '#9aa0a6', flexShrink: 0 }} />
                        <Typography sx={{ fontSize: '0.82rem', color: '#5f6368' }}>
                          <span style={{ color: '#1a73e8', fontWeight: 500 }}>{op}:</span> {searchValue}
                        </Typography>
                      </ListItem>
                    );
                  })}
                </Box>
              )}

              {/* Search current query */}
              {searchValue.trim() && (
                <Box sx={{ borderTop: '1px solid #f1f3f4' }}>
                  <ListItem onClick={() => executeSearch(searchValue)}
                    sx={{ px: 2, py: 0.9, cursor: 'pointer', '&:hover': { bgcolor: '#f1f3f4' }, gap: 1.5 }}
                  >
                    <SearchIcon sx={{ fontSize: 16, color: '#1a73e8', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.875rem', color: '#1a73e8', fontWeight: 500 }}>
                      Search for "{searchValue}"
                    </Typography>
                  </ListItem>
                </Box>
              )}
            </Paper>
          )}
        </Box>
      </ClickAwayListener>
      ) : (
        /* Mobile search icon */
        <Tooltip title="Search" arrow>
          <IconButton
            onClick={() => {
              // TODO: Open search dialog/page for mobile
              console.log('Mobile search clicked');
            }}
            sx={{
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              mr: 0.5,
              color: '#4a5568',
              '&:hover': { background: '#f3f4f6' },
            }}
          >
            <SearchIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {/* Action Icons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 0.25 }}>

        {/* Notifications */}
        <Tooltip title="Notifications" arrow>
          <IconButton
            onClick={(e) => { setNotifAnchor(e.currentTarget); setNotifOpen(true); }}
            sx={{
              width: isMobile ? '40px' : '32px',
              height: isMobile ? '40px' : '32px',
              borderRadius: '8px',
              color: '#4a5568',
              '&:hover': { background: '#f3f4f6' },
            }}
          >
            <Badge badgeContent={unreadCount > 0 ? unreadCount : null} color="error" max={99}
              sx={{ '& .MuiBadge-badge': { fontSize: '10px', height: 16, minWidth: 16, top: 2, right: 2 } }}>
              <NotificationsIcon sx={{ fontSize: isMobile ? 20 : 18 }} />
            </Badge>
          </IconButton>
        </Tooltip>

        {/* Notification Dropdown */}
        {notifOpen && (
          <ClickAwayListener onClickAway={() => setNotifOpen(false)}>
            <Paper
              elevation={4}
              sx={{
                position: 'fixed',
                top: isMobile ? '56px' : '60px',
                right: isMobile ? '8px' : '80px',
                width: isMobile ? 'calc(100vw - 16px)' : 360,
                maxWidth: 360,
                maxHeight: isMobile ? 'calc(100vh - 64px)' : 480,
                borderRadius: '12px',
                border: '1px solid #e0e4e8',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                zIndex: 1400,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: '1px solid #e8eaed' }}>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem', color: '#1f2937' }}>
                  Notifications {unreadCount > 0 && <Box component="span" sx={{ ml: 0.75, px: 0.75, py: 0.1, bgcolor: '#ef4444', color: '#fff', borderRadius: '10px', fontSize: '0.7rem', fontWeight: 700 }}>{unreadCount}</Box>}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  {unreadCount > 0 && (
                    <Tooltip title="Mark all read" arrow>
                      <IconButton size="small" onClick={markAllAsRead} sx={{ color: '#1a73e8', p: 0.5 }}>
                        <DoneAllIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {notifications.length > 0 && (
                    <Tooltip title="Clear all" arrow>
                      <IconButton size="small" onClick={clearAll} sx={{ color: '#5f6368', p: 0.5 }}>
                        <ClearAllIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </Box>

              {/* List */}
              <Box sx={{ overflowY: 'auto', flex: 1 }}>
                {notifications.length === 0 ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 5, color: '#9ca3af' }}>
                    <NotificationsIcon sx={{ fontSize: 40, mb: 1, opacity: 0.4 }} />
                    <Typography sx={{ fontSize: '0.875rem' }}>No notifications</Typography>
                  </Box>
                ) : (
                  <List disablePadding>
                    {notifications.map((n, idx) => (
                      <React.Fragment key={n.id}>
                        <ListItem
                          onClick={() => {
                            markAsRead(n.id);
                            if (n.data?.emailId && n.data?.folderId) {
                              navigate(`/mail/${n.data.folderId}`);
                              setNotifOpen(false);
                            }
                          }}
                          sx={{
                            px: 2, py: 1.25,
                            cursor: n.data?.emailId ? 'pointer' : 'default',
                            bgcolor: n.read ? 'transparent' : '#f0f7ff',
                            '&:hover': { bgcolor: '#f3f4f6' },
                            alignItems: 'flex-start',
                            gap: 1,
                          }}
                        >
                          <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: n.read ? 'transparent' : '#1a73e8', mt: 0.75, flexShrink: 0 }} />
                          <ListItemText
                            primary={<Typography sx={{ fontSize: '0.85rem', fontWeight: n.read ? 400 : 600, color: '#1f2937', lineHeight: 1.4 }}>{n.title}</Typography>}
                            secondary={
                              <Box>
                                <Typography sx={{ fontSize: '0.78rem', color: '#6b7280', mt: 0.25 }}>{n.message}</Typography>
                                <Typography sx={{ fontSize: '0.72rem', color: '#9ca3af', mt: 0.5 }}>
                                  {new Date(n.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {idx < notifications.length - 1 && <Divider sx={{ mx: 2 }} />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </Box>
            </Paper>
          </ClickAwayListener>
        )}

        {/* Admin Panel Icon - only for admin/superadmin */}
        {(user?.role === 'admin' || user?.role === 'superadmin') && (
          <Tooltip title="Admin Panel" arrow>
            <IconButton
              onClick={() => {
                // Share token to localStorage so admin panel (same origin) reads it directly
                const token = localStorage.getItem('accessToken');
                const refresh = localStorage.getItem('refreshToken');
                if (token) localStorage.setItem('token', token);
                if (refresh) localStorage.setItem('refreshToken', refresh);
                window.open('/admin/dashboard', '_blank');
              }}
              sx={{
                width: isMobile ? '40px' : '32px',
                height: isMobile ? '40px' : '32px',
                borderRadius: '8px',
                color: '#7c3aed',
                '&:hover': { background: '#f5f3ff' },
              }}
            >
              <AdminPanelIcon sx={{ fontSize: isMobile ? 21 : 19 }} />
            </IconButton>
          </Tooltip>
        )}

        {/* User Avatar */}
        <Avatar
          onClick={(e) => setUserMenuAnchor(e.currentTarget)}
          sx={{
            width: isMobile ? '36px' : '32px',
            height: isMobile ? '36px' : '32px',
            background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)',
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.8rem',
            cursor: 'pointer',
            ml: 0.5,
          }}
        >
          {getInitials(user?.email || 'U')}
        </Avatar>

        {/* User Menu */}
        <Menu
          anchorEl={userMenuAnchor}
          open={Boolean(userMenuAnchor)}
          onClose={() => setUserMenuAnchor(null)}
          PaperProps={{
            sx: {
              mt: 1,
              borderRadius: '8px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
              border: '1px solid #e0e4e8',
            },
          }}
        >
          <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/profile'); }}>
            Profile
          </MenuItem>
          <MenuItem onClick={() => { setUserMenuAnchor(null); navigate('/settings'); }}>
            Settings
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>Logout</MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};

export default HybridHeader;
